import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
} from '@microsoft/signalr';
import { from, Observable, of, Subject, Subscription, timer } from 'rxjs';
import {
    catchError,
    delay,
    delayWhen,
    mapTo,
    retryWhen,
    scan,
    switchMap,
    tap,
} from 'rxjs/operators';

import { AuthStatus } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';

export abstract class DefaultSocketService {
    readonly didCreate: Observable<void>;
    readonly didConnect: Observable<void>;
    readonly didDisconnect: Observable<boolean>;

    protected readonly _didCreateSubj: Subject<void>;
    protected readonly _didConnectSubj: Subject<void>;
    protected readonly _didDisconnectSubj: Subject<boolean>;
    protected _subSub: Subscription;
    protected _conn: HubConnection;
    protected _stopped: boolean;

    get created(): boolean {
        return this._conn != null;
    }

    get connected(): boolean {
        return this._conn?.state === HubConnectionState.Connected;
    }

    constructor(
        private readonly _authService: AuthService,
        bind: boolean = true,
    ) {
        this._didCreateSubj = new Subject();
        this._didConnectSubj = new Subject();
        this._didDisconnectSubj = new Subject();

        this.didCreate = this._didCreateSubj.asObservable();
        this.didConnect = this._didConnectSubj.asObservable();
        this.didDisconnect = this._didDisconnectSubj.asObservable();

        if (bind) {
            this._bindAuth();
        }
    }

    on(methodName: string, newMethod: (...args: any[]) => void): void {
        this._conn?.on(methodName, newMethod);
    }

    off(methodName: string): void {
        this._conn?.off(methodName);
    }

    async send(methodName: string, ...args: any[]): Promise<void> {
        await this._conn?.send(methodName, ...args);
    }

    protected abstract _websocketUrl(): string;

    private _bindAuth(): void {
        this._subSub = this._authService.authStatus
            .pipe(
                /* offset initial synchronous behaviour subject emit */
                delay(0),
                switchMap((authStatus) => {
                    if (authStatus === AuthStatus.AUTHENTICATED) {
                        return from(this._bindSocket());
                    } else {
                        return from(this._unbindSocket());
                    }
                }),
            )
            .subscribe();
    }

    private async _bindSocket(): Promise<void> {
        await this._create(
            this._websocketUrl(),
            () => this._authService.user.token.access_token,
        );

        void this._start();
    }

    private async _unbindSocket(): Promise<void> {
        void this.stop();
    }

    private async _create(
        url: string,
        accessTokenFactory?: () => string | Promise<string>,
    ): Promise<void> {
        await this.stop();

        this._conn = new HubConnectionBuilder()
            .withUrl(url, { accessTokenFactory })
            .build();

        this._conn.onclose(() => {
            this._didDisconnectSubj.next(this._stopped);
            if (!this._stopped) {
                void this._start();
            }
        });

        this._didCreateSubj.next();
    }

    private async _start(): Promise<boolean> {
        this._stopped = false;
        if (
            this._conn == null ||
            this._conn.state !== HubConnectionState.Disconnected
        ) {
            return false;
        }
        let currentDelay = 750;
        return await of(null)
            .pipe(
                switchMap(() => from(this._conn.start())),
                retryWhen((errors) =>
                    errors.pipe(
                        delayWhen(() => timer(currentDelay)),
                        scan((acc) => {
                            if (this._stopped) {
                                throw new Error('retry cancelled');
                            }
                            currentDelay += currentDelay / 3;
                            console.log(`retry ${acc + 1}`);
                            return acc + 1;
                        }, 0),
                    ),
                ),
                mapTo(true),
                catchError((err) => {
                    console.log(err);
                    return of(false);
                }),
                tap((ret) => {
                    if (ret) {
                        this._didConnectSubj.next();
                    }
                }),
            )
            .toPromise();
    }

    private async stop(): Promise<boolean> {
        this._stopped = true;
        if (this._conn == null) {
            return false;
        }
        return await of(null)
            .pipe(
                switchMap(() => from(this._conn.stop())),
                mapTo(true),
                catchError((err) => {
                    console.log(err);
                    return of(false);
                }),
            )
            .toPromise();
    }

    protected _destroy(): void {
        this._subSub?.unsubscribe();
        this._didDisconnectSubj?.complete();
        this._didConnectSubj?.complete();
        this._didCreateSubj?.complete();
    }
}
